/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
import { cva, VariantProps } from 'class-variance-authority'
import { CheckIcon, XIcon } from 'lucide-react'
import { HTMLAttributes } from 'react'

import { cn } from '@/lib/utils'

type StepItem = {
  description?: string
  title: string
}

type StepperProps = {
  activeIndex: number
  onSelectStepIndex?(index: number): void
  steps: StepItem[]
}

const Stepper = ({ activeIndex, onSelectStepIndex, steps }: StepperProps) => {
  return (
    <ol className="space-y-3 w-72">
      {steps?.map((step, index) => {
        const getStepState = (): VariantProps<typeof stepVariants>['state'] => {
          if (index < activeIndex) return 'completed'
          if (index === activeIndex) return 'active'

          return 'default'
        }

        return (
          <Step
            description={step.description}
            key={step.title}
            onClick={() => onSelectStepIndex?.(index)}
            state={getStepState()}
            stepNumber={index + 1}
            title={step.title}
          />
        )
      })}
    </ol>
  )
}

const stepVariants = cva(
  'w-full transition-all p-4 bg-success-soft border rounded-md cursor-pointer',
  {
    defaultVariants: {
      state: 'default',
    },
    variants: {
      state: {
        active: 'bg-info-bg border-info-border text-info hover:bg-info-hover',
        completed: 'bg-brand-funkia/10 border-brand-funkia/30 text-brand-funkia hover:bg-brand-funkia/15',
        default: 'bg-layer-1 border text-foreground hover:bg-layer-2 hover:border-info-border',
        skipped: 'bg-muted border text-muted-foreground hover:bg-background',
      },
    },
  },
)

const indicatorVariants = cva(
  'flex size-6 flex-shrink-0 items-center justify-center rounded-full text-center text-sm font-semibold',
  {
    defaultVariants: {
      state: 'default',
    },
    variants: {
      state: {
        active: 'bg-info-solid text-info-solid-fg font-bold',
        completed: 'text-brand-funkia',
        default: 'text-text-secondary',
        skipped: '',
      },
    },
  },
)

type StepProps = {
  description?: string
  stepNumber: number
  title: string
} & HTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof stepVariants>

const Step = ({ description, state, stepNumber, title, ...props }: StepProps) => {
  const getIcon = () => {
    switch (state) {
      case 'completed':
        return <CheckIcon />
      case 'skipped':
        return <XIcon />
      default:
        return null
    }
  }

  return (
    <li>
      <button {...props} className={cn(stepVariants({ state }))} role="alert" type="button">
        <span className="sr-only">{title}</span>
        <div className="flex items-center gap-0 justify-between">
          <div className="text-left flex-1 flex items-center gap-4">
            <div className={cn(indicatorVariants({ state }))}>{stepNumber}</div>
            <h3 className="font-medium line-clamp-1">{title}</h3>
            {description && <p className="text-sm line-clamp-2">{description}</p>}
          </div>
          <span className="[&_svg]:size-4">{getIcon()}</span>
        </div>
      </button>
    </li>
  )
}

export { Step, Stepper }
